import { useEffect, useState } from "react";
import { Form, Image, Typography, Card, Checkbox, Flex, Alert } from "antd";
import CustomInput from "../../components/CustomInput/Input";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CustomButton from "../../components/CustomButton/Button";
import CustomDatePicker from "../../components/CustomDatePicker/CustomDatePicker";
import {
  nameRules,
  phoneNumberRules,
  capitalizeWords,
} from "../../helpers/helpers";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllRelations,
  addNomineeDetails,
} from "../../redux/slices/ProfileSlice";
import { BTN_TXT, ERROR_MSGS, TITLE_TXT } from "../../constants/constants";
import CountryCode from "../../components/CountryCodeSelect/CountryCode";
import Logo from "../../assets/images/auth-logo.png";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "./Nominee.scss";

const { Title } = Typography;

const Nominee = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { relations, nomineeLoading, nomineeError } = useSelector(
    (state) => state.profile,
  );
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isConfirmed, setIsConfirmed] = useState(false);

  useEffect(() => {
    dispatch(getAllRelations());
  }, [dispatch]);

  const handleFieldsChange = (_, allFields) => {
    const allFieldsFilled = allFields.every(
      (field) => field.errors.length === 0 && field.value,
    );
    setIsButtonDisabled(!(allFieldsFilled && isConfirmed));
  };

  const onSubmit = async (values) => {
    const payload = {
      orgId: user?.orgId,
      userId: user?.userId,
      relationId: values.relationship,
      dob: values.nomineeDob.format("YYYY-MM-DD"),
      name: values.nomineeName,
      phoneNumber: values.phoneNumber,
    };
    const res = await dispatch(addNomineeDetails(payload));
    if (res.payload?.statusCode === 201) {
      form.resetFields();
      navigate("/sla");
    }
  };

  return (
    <div className="nominee-details-container">
      {/* Logo stays fixed */}
      <div className="logo">
        <Image preview={false} src={Logo} alt="Logo" />
      </div>

      {/* Centered Card */}
      <Card className="nominee-card">
        <Title level={3}>{TITLE_TXT.NOM_DETS}</Title>

        <Form
          layout="vertical"
          form={form}
          onFinish={onSubmit}
          onFieldsChange={handleFieldsChange}
          initialValues={{ countryCode: "91" }}
          className="nominee-form"
        >
          <CustomInput
            label="Nominee Legal Name"
            name="nomineeName"
            placeholder="Enter Name"
            type="text"
            rules={nameRules("")}
          />
          <CustomDatePicker
            label="Nominee Date of Birth"
            name="nomineeDob"
            placeholder="Enter DOB"
            isRequired={true}
            rules={[{ required: true, message: ERROR_MSGS.ENTER_NOM_DOB }]}
            disabledDate={(current) =>
              current && current > dayjs().endOf("day")
            }
          />
          <CustomSelect
            label="Relationship"
            name="relationship"
            className="custom-relation-select"
            placeholder="Select Relation"
            options={relations.map((rel) => ({
              value: rel.relationId,
              label: capitalizeWords(rel.relationName),
            }))}
            rules={[{ required: true, message: ERROR_MSGS.SEL_REL }]}
          />
          <CustomInput
            label="Phone Number"
            name="phoneNumber"
            placeholder="Enter Phone Number"
            addonBefore={<CountryCode />}
            rules={phoneNumberRules("")}
          />
          {nomineeError && (
            <Alert message={nomineeError} type="error" showIcon />
          )}
          <Checkbox
            checked={isConfirmed}
            onChange={(e) => {
              setIsConfirmed(e.target.checked);

              const noErrors = form
                .getFieldsError()
                .every((f) => f.errors.length === 0);
              const values = form.getFieldsValue();
              const allFilled = Object.values(values).every((v) => v);

              setIsButtonDisabled(!(noErrors && allFilled && e.target.checked));
            }}
            style={{ marginBottom: 15, marginTop: 10, fontSize: 12 }}
          >
            I/We nominate the person mentioned above to receive all amounts
            related to the Invoice financing facility availed by KARCNY Company
            in the event of my/our death. The financial institution may release
            or transfer these amounts to the nominee. This nomination applies to
            all receivables, outstanding invoices, settlements, and balances
            related to the Invoice financing facility.
          </Checkbox>
          <Flex justify="space-between" align="center" className="form-buttons">
            <CustomButton
              variant="outlined"
              color="primary"
              onClick={() => navigate("/onboarding")}
            >
              {BTN_TXT.MAYBE_LATER}
            </CustomButton>
            <CustomButton
              type="primary"
              htmlType="submit"
              disabled={isButtonDisabled}
              loading={nomineeLoading}
            >
              {BTN_TXT.SUBMIT}
            </CustomButton>
          </Flex>
        </Form>
      </Card>
    </div>
  );
};

export default Nominee;
